import React from 'react'
import styled from 'styled-components'
import moment from 'moment'
import Cell from './Cell'
import findDiffs from './helpers/findDiffs'
import fillNullDiffs from './helpers/fillNullDiffs'
import fillNullCashflowData from './helpers/fillNullCashflowData'
import findDiscrepancies from './helpers/findDiscrepancies'
const Container = styled.div`
  border: 2px solid #eeeeee;
`
const Category = styled.div`
  display: grid;
  grid-template-columns: 2fr 7fr;
  grid-template-rows: auto auto;
  grid-column-gap: 0px;
  border-top: 9px solid #f8f8f8;
  border-bottom: 2px solid #dddddd;
  background: #EEEEEE;
`
const Title = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  background: #dfdfdf;
  padding: 0px 10px;
  text-align: center;
  grid-column-start: 1;
  font-weight: bold;
  height: 100%;
  grid-row-start: 1;
  grid-row-end: 2;
  grid-area: span 2;
`
const Row = styled.div`
  display: grid;
  grid-template-columns: 2fr repeat(5, 1fr);
  padding: 10px 10px 10px 0px;
  background: #EEEEEE;
`
const Header = styled.div`
  display: grid;
  grid-template-columns: 4fr repeat(5, 1fr);
  padding: 10px 10px 10px 0px;
`
const Year = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  font-weight: bold;
`
const Small = styled.small`
  color: #888;
  margin-top: none;
`
const Reason = Row.extend`
  display: block;
  grid-column-start: 2;
  grid-column-end: 6;
  background: white;
  padding: 6px;
`
const Spacer = styled.div`
  background : #dfdfdf;
  width: 100%;
`
const Silver = styled.small`
  color: silver;
`

export default class Adjustments extends React.Component {
  renderYears = () => {
    const { years } = this.props
    return Array.from(years.keys()).map(year => {
      return <Year key={`adjustments-${year}`}>{year}</Year>
    })
  }
  getReasonByCategory = category => {
    const { cashflowData } = this.props.entity
    const data = cashflowData.filter(datum => datum.category === category)
    for (const datum of data) {
      if (datum.reason) {
        return datum.reason
      }
    }
  }
  renderDiffsByCategory = (category) => {
    const { years } = this.props
    const { cashflowData, fiscalYears } = this.props.entity
    const diffs = findDiffs(cashflowData, fiscalYears).filter(diff => diff.category === category.key)
    if (diffs.length > 0 && diffs.some(diff => diff.diff > 0 || diff.diff < 0)) {
      return (
        <Category key={`adjustments-${category.key}`}>
          <Title>
            {category.title}
          </Title>
          <Row>
            <div />
            {
              fillNullDiffs(diffs, years).map((diff, i) => {
                return (
                  <Cell key={`adjustments-${category}-cell-${i}`} data={diff.diff > 0 || diff.diff < 0 ? diff.diff : null} />
                )
              })
            }
          </Row>
          <Spacer />
          <Reason>
            <small>
              <b>Reason  </b>
              {this.getReasonByCategory(category.key)} <br />
              <Silver>Last edited by { diffs[0].accountable } on { diffs[0].lastEdited }</Silver>
            </small>
          </Reason>
        </Category>
      )
    }
  }
  renderAdjustmentsByCategory = (category) => {
    const { years } = this.props
    const { cashflowData } = this.props.entity
    const data = cashflowData.filter(datum => datum.category === category.key)
    const mostRecentDatum = data.sort((a, b) => b.createdAt - a.createdAt)[0]
    if (data.length > 0 && data.some(datum => datum.data > 0 || datum.data < 0)) {
      return (
        <Category key={`adjustments-${category.key}`}>
          <Title>
            {category.title}
          </Title>
          <Row>
            <div />
            {
              fillNullCashflowData(data, years).map((datum, i) => {
                if (datum.data > 0 || datum.data < 0) {
                  return (
                    <Cell key={`adjustments-${category.key}-cell-${i}`} data={datum.data} />
                  )
                } else {
                  return <Cell key={`adjustments-${category.key}-cell-${i}`} data={null} />
                }
              })
            }
          </Row>
          <Spacer />
          <Reason>
            <small>
              <b>Reason  </b>
              {this.getReasonByCategory(category.key)} <br />
              <Silver>Last edited by {mostRecentDatum.accountable.name} on {moment.unix(mostRecentDatum.createdAt).format('MM/DD/YYYY')}</Silver>
            </small>
          </Reason>
        </Category>
      )
    }
  }
  renderSegments = () => {
    const { financialReviewSegments } = this.props.entity
    const allCategories = financialReviewSegments.reduce((acc, segment) => acc.concat(segment.categories), [])
    const segments = []
    for (const category of allCategories) {
      if (category.required && !category.derived) {
        segments.push(this.renderDiffsByCategory(category))
      } else if (!category.required && !category.derived) {
        segments.push(this.renderAdjustmentsByCategory(category))
      }
    }
    return segments
  }
  renderAdjustments = () => {
    // renderAdjustmentsByCategory and renderDiffsByCategory don't return anything unless they find something that needs to be rendered
    // if segments.length is zero, that means there's no diffs or discrepancies, so show alt message
    const { cashflowData, financialReviewSegments, fiscalYears } = this.props.entity
    const segments = this.renderSegments()
    const areThereDiffs = findDiffs(cashflowData, fiscalYears).some(diff => diff.diff > 0 || diff.diff < 0)
    const areThereDiscrepancies = findDiscrepancies(cashflowData).some(discrepancy => discrepancy.diff > 0 || discrepancy.diff < 0)
    const allCategories = financialReviewSegments.reduce((acc, segment) => acc.concat(segment.categories), [])
    const nonRequiredCategories = allCategories.filter(cat => !cat.required && !cat.derived).reduce((acc, cat) => acc.set(cat.key, cat), new Map())
    const areThereAdditionalCategories = cashflowData.some(datum => nonRequiredCategories.has(datum.category)) && Array.from(nonRequiredCategories.values()).some(cat => cashflowData.filter(datum => datum.category === cat.key).some(datum => datum.data > 0 || datum.data < 0))
    if (areThereDiffs /*|| areThereDiscrepancies*/ || areThereAdditionalCategories) {
      return (
        <Container>
          <Header>
            <div />
            {this.renderYears()}
          </Header>
          {segments}
        </Container>
      )
    } else {
      return (
        <Small>
            Adjustments made prior to receiving tax transcript data will be automatically applied during decisioning.
        </Small>
      )
    }
  }
  render() {
    const { name } = this.props.entity
    return (
      <section ref={this.props.innerRef} id="adjustments">
        <h3>Cash and Debt Adjustments for {`${name}`}</h3>
        {this.renderAdjustments()}
      </section>
    )
  }
}